import { useCallback, useEffect, useMemo, useRef } from 'react';
import { alpha, Button, IconButton, Stack, styled, Typography, useTheme } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridRowsProp,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { IProject, IReport } from 'src/@types/apiResponseTypes';
import { EditorView } from 'src/components/block-editor';
import Iconify from 'src/components/iconify';
import ReportComments from 'src/components/report-comments/ReportComments';
import RndChip from 'src/components/rnd-chip';
import { MultiFilePreview, TUploadFile } from 'src/components/upload';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { useStores } from 'src/models';
import { LOCAL_STORAGE_KEY } from 'src/static/constants';
import { downloadFile, FILE_DOWNLOAD_API_URL } from 'src/utils/downloadFile';
import { fDate, fDateTime } from 'src/utils/formatTime';

/**
 * ## TableTypeWorkReportItem 설명
 * - 업무보고서 카드 컴포넌트
 * - DataGridPro 사용하여 테이블 형식으로 표시
 * - 컬럼 리사이즈, 토글, sticky header 지원
 */
interface ITableTypeWorkReportItemProps {
  report: IReport;
  onCopyReport?: (reportSid: number) => void;
  onEditReport?: (reportSid: number) => void;
  onExport?: (report: IReport) => void;
  viewProjectInfo?: (project: IProject) => void;
  isFullscreen?: boolean;
  fullscreenContainer?: HTMLElement | null;
}

function TableTypeWorkReportItem({
  report,
  onCopyReport,
  onEditReport,
  onExport,
  viewProjectInfo,
  isFullscreen = false,
  fullscreenContainer,
}: ITableTypeWorkReportItemProps) {
  const theme = useTheme();
  const { authStore } = useStores();
  const apiRef = useGridApiRef();

  // 테이블 컨테이너 감지를 위한 ref와 state
  const dataGridContainerRef = useRef<HTMLDivElement>(null);

  // localStorage에서 컬럼 설정 불러오기 (전체 테이블 공통)
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useLocalStorage<GridColumnVisibilityModel>(`${LOCAL_STORAGE_KEY.WORK_REPORT_TABLE_COLUMNS}`, {
      projectNm: true, // 프로젝트명
      customerCmpy: false, // 고객사
      pm: false, // PM
      startDay: false, // 시작일
      endDay: false, // 종료일
      content: true, // 실적
      content2: true, // 계획
      issueContent: true, // 보고/이슈
    });

  // localStorage에서 컬럼 width 설정 불러오기 (전체 테이블 공통)
  const [columnWidths, setColumnWidths] = useLocalStorage<Record<string, number>>(
    `${LOCAL_STORAGE_KEY.WORK_REPORT_TABLE_COLUMN_WIDTHS}`,
    {
      projectNm: 150, // 프로젝트명
      customerCmpy: 150, // 고객사
      pm: 100, // PM
      startDay: 120, // 시작일
      endDay: 120, // 종료일
      content: 500, // 실적
      content2: 500, // 계획
      issueContent: 500, // 보고/이슈
    },
  );

  // 첨부파일 다운로드
  const handleDownloadFile = (file: TUploadFile) => {
    downloadFile({
      file: file,
      apiUrl:
        typeof file === 'object' && 'fileSid' in file
          ? FILE_DOWNLOAD_API_URL.report(file.reportSid, file.fileSid)
          : undefined,
    });
  };

  // DataGrid rows 생성
  const rows: GridRowsProp = useMemo(() => {
    return (
      report.contents?.map((content, index) => {
        const isRnd = content.rndProjectSid != null;
        const isEtc = !isRnd && !content.project;
        const projectNm = isRnd
          ? content.rndProject?.projectNm || ''
          : isEtc
          ? '기타'
          : content.project?.projectNm || '';
        return {
          id: content.projectSid || content.rndProjectSid || `etc-${index}`,
          projectSid: content.projectSid,
          rndProjectSid: content.rndProjectSid,
          isRnd,
          projectNm,
          customerCmpy: isRnd ? '-' : content.project?.customerCmpy?.company || '-',
          pm:
            !isRnd && content.project?.pmList && content.project.pmList.length > 0
              ? content.project?.pmList[0].empNm || '-'
              : '-',
          startDay: !isRnd && content.project?.startDay ? fDate(content.project.startDay) : '-',
          endDay: !isRnd && content.project?.endDay ? fDate(content.project.endDay) : '-',
          content: content.content,
          content2: content.content2,
          issueContent: content.issueContent || '',
          project: content.project,
        };
      }) || []
    );
  }, [report.contents]);

  // 다른 테이블의 컬럼 설정 변경을 감지하여 동기화
  useEffect(() => {
    const handleColumnsChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        setColumnVisibilityModel(customEvent.detail);
      }
    };

    const handleColumnWidthsChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        setColumnWidths(customEvent.detail);
      }
    };

    window.addEventListener('workReportTableColumnsChanged', handleColumnsChanged);
    window.addEventListener('workReportTableColumnWidthsChanged', handleColumnWidthsChanged);

    return () => {
      window.removeEventListener('workReportTableColumnsChanged', handleColumnsChanged);
      window.removeEventListener('workReportTableColumnWidthsChanged', handleColumnWidthsChanged);
    };
  }, [setColumnVisibilityModel, setColumnWidths]);

  // DataGrid columns 정의
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'projectNm',
        headerName: '프로젝트명',
        width: columnWidths.projectNm || 300,
        minWidth: 200,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {params.row.isRnd && <RndChip />}
            <Typography variant="body2" fontWeight="bold">
              {params.value}
            </Typography>
            {!params.row.isRnd && params.row.project?.projectSid && (
              <IconButton
                size="small"
                sx={{ width: 24, height: 24, p: 0 }}
                onClick={() => viewProjectInfo && viewProjectInfo(params.row.project)}
              >
                <Iconify icon="ic:outline-info" width={16} />
              </IconButton>
            )}
          </Stack>
        ),
      },
      {
        field: 'customerCmpy',
        headerName: '고객사',
        width: columnWidths.customerCmpy || 150,
        minWidth: 100,
      },
      {
        field: 'pm',
        headerName: 'PM',
        width: columnWidths.pm || 100,
        minWidth: 80,
      },
      {
        field: 'startDay',
        headerName: '시작일',
        width: columnWidths.startDay || 120,
        minWidth: 100,
      },
      {
        field: 'endDay',
        headerName: '종료일',
        width: columnWidths.endDay || 120,
        minWidth: 100,
      },
      {
        field: 'content',
        headerName: '실적',
        width: columnWidths.content || 250,
        minWidth: 200,
        renderCell: (params) => (
          <EditorCellWrapper>
            <EditorView content={params.value} />
          </EditorCellWrapper>
        ),
      },
      {
        field: 'content2',
        headerName: '계획',
        width: columnWidths.content2 || 250,
        minWidth: 200,
        renderCell: (params) => (
          <EditorCellWrapper>
            <EditorView content={params.value} />
          </EditorCellWrapper>
        ),
      },
      {
        field: 'issueContent',
        headerName: '보고/이슈',
        width: columnWidths.issueContent || 250,
        minWidth: 200,
        renderCell: (params) => (
          <EditorCellWrapper>
            <EditorView content={params.value} />
          </EditorCellWrapper>
        ),
      },
    ],
    [columnWidths, viewProjectInfo],
  );

  // 컬럼 width 변경 시 localStorage에 저장 및 다른 테이블에 알림
  const handleColumnResize = useCallback(
    (params: GridColumnResizeParams) => {
      setColumnWidths((prev: Record<string, number>) => {
        const newWidths = {
          ...prev,
          [params.colDef.field]: params.width,
        };
        // 다른 테이블에 변경 알림 (새로운 width 데이터 포함)
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('workReportTableColumnWidthsChanged', { detail: newWidths }),
          );
        }, 0);
        return newWidths;
      });
    },
    [setColumnWidths],
  );

  // 컬럼 visibility 변경 시 localStorage에 저장 및 다른 테이블에 알림
  const handleColumnVisibilityChange = useCallback(
    (newModel: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(newModel);
      // 다른 테이블에 변경 알림 (새로운 visibility model 포함)
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('workReportTableColumnsChanged', { detail: newModel }),
        );
      }, 0);
    },
    [setColumnVisibilityModel],
  );

  return (
    <Container>
      <TopContainer>
        <Typography variant="h4" textAlign="center">
          {report.title}
          {/* 제출 전 보고서인 경우 표시 */}
          {report.regUser.userSid === authStore.user?.userSid && report.submitYn === false && (
            <BeforeSubmitTag>제출 전</BeforeSubmitTag>
          )}
        </Typography>
        <FlexRowContainer>
          <RegUserInfoContainer>
            <Stack>
              <Stack direction={'row'} alignItems={'baseline'} spacing={1}>
                <Typography variant="subtitle1" color={theme.palette.grey[900]}>
                  {report.regUser.userNm}
                </Typography>
                <Typography variant="caption" color={theme.palette.grey[800]}>
                  {fDateTime(report.regDt)}
                </Typography>
              </Stack>
              <Typography variant="caption" color={theme.palette.grey[800]}>
                {`comments: ${report.comments?.length || 0}`}
              </Typography>
            </Stack>
          </RegUserInfoContainer>
          <Stack direction={'row'} spacing={1}>
            {onCopyReport && (
              <Button
                size={'small'}
                color={'secondary'}
                variant={'outlined'}
                startIcon={<Iconify icon="mingcute:copy-fill" />}
                sx={{ ml: 1 }}
                onClick={() => onCopyReport(report.reportSid)}
              >
                복사
              </Button>
            )}
            {onExport && (
              <Button
                size={'small'}
                color={'success'}
                variant={'outlined'}
                startIcon={<Iconify icon="ri:file-excel-2-line" />}
                sx={{ ml: 1 }}
                onClick={() => onExport(report)}
              >
                저장
              </Button>
            )}
            {/* 작성자인 경우 수정버튼 노출 */}
            {onEditReport && report.regUser.userSid === authStore.user?.userSid && (
              <EditReportButton
                size={'small'}
                variant="outlined"
                onClick={() => onEditReport(report.reportSid)}
              >
                수정
              </EditReportButton>
            )}
          </Stack>
        </FlexRowContainer>
      </TopContainer>

      {/* 고정 헤더 (테이블 헤더가 화면에서 사라질 때 표시) */}

      {/* DataGridPro 테이블 */}
      <DataGridContainer ref={dataGridContainerRef}>
        <DataGridPro
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          onColumnWidthChange={handleColumnResize}
          disableRowSelectionOnClick
          hideFooter
          autoHeight
          slotProps={{
            basePopper: {
              container: isFullscreen && fullscreenContainer ? fullscreenContainer : undefined,
            },
          }}
          getRowHeight={() => 'auto'}
          sx={{
            border: 'none',
            '.MuiDataGrid-container--top [role=row]': {
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              borderRadius: 0,
            },
            '& .MuiDataGrid-cell': {
              border: `1px solid ${theme.palette.divider}`,
              padding: theme.spacing(1),
              display: 'flex',
              alignItems: 'flex-start',
            },
            '& .MuiDataGrid-row:hover': {
              color: theme.palette.text.primary,
              backgroundColor: 'transparent',
            },
          }}
        />
      </DataGridContainer>

      {report.fileList.length > 0 && (
        <AttachmentFileListContainer>
          <StyledLabel>첨부된 파일 목록</StyledLabel>
          <MultiFilePreview files={report.fileList} onDownload={handleDownloadFile} />
        </AttachmentFileListContainer>
      )}
      <ReportComments
        reportSid={report.reportSid}
        sx={{ marginTop: 2 }}
        initComments={report.comments || undefined}
      />
    </Container>
  );
}

export default TableTypeWorkReportItem;

const StyledInnerContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  flex: 1,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: `${theme.spacing(4)} ${theme.spacing(2)}`,
}));

const AttachmentFileListContainer = styled(StyledInnerContainer)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const StyledLabel = styled('span')(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 700,
  padding: theme.spacing(0, 1),
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: '-12px',
  left: '20px',
  background: theme.palette.background.paper,
}));

const Container = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  padding: theme.spacing(3, 2),
}));

const TopContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  marginBottom: theme.spacing(2),
}));

const EditReportButton = styled(Button)(({ theme }) => ({}));

const DataGridContainer = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  overflowX: 'auto',
}));

const BeforeSubmitTag = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  padding: theme.spacing(0.5, 1),
  fontSize: '0.75rem',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.primary.main}`,
  marginLeft: theme.spacing(1),
}));

const FlexRowContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const RegUserInfoContainer = styled('div')(({ theme }) => ({}));

const EditorCellWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(0.5, 0),
  textAlign: 'left',
}));
